/**
 * ตัวช่วยของหน้าจอ OAuth client (SSO) — ฟอร์ม <-> body ของ `POST`/`PATCH`
 * ไม่รู้จัก React และไม่แตะ network
 */

import {
  REDIRECT_URI_KINDS,
  type CreateOauthClientBody,
  type OauthClient,
  type OauthClientRedirectUri,
  type RedirectUriKind,
  type UpdateOauthClientBody,
} from '../../lib/rbacAdmin/types';

export const REDIRECT_URI_KIND_LABELS: Readonly<Record<RedirectUriKind, string>> = {
  LOGIN_CALLBACK: 'Callback หลังล็อกอิน',
  POST_LOGOUT: 'ปลายทางหลังออกจากระบบ',
};

export const DEFAULT_ACCESS_TOKEN_TTL_SECONDS = 900;
export const DEFAULT_ID_TOKEN_TTL_SECONDS = 600;
export const DEFAULT_REFRESH_ABSOLUTE_TTL_SECONDS = 43200;
export const DEFAULT_REFRESH_IDLE_TTL_SECONDS = 1800;

export interface OauthClientFormState {
  readonly clientId: string;
  readonly appCode: string;
  readonly clientName: string;
  readonly redirectUris: readonly OauthClientRedirectUri[];
  readonly mfaRoles: readonly string[];
  readonly accessTtl: string;
  readonly idTtl: string;
  readonly refreshEnabled: boolean;
  readonly refreshAbsoluteTtl: string;
  readonly refreshIdleTtl: string;
  readonly requireAppRole: boolean;
  readonly active: boolean;
}

export function emptyOauthClientForm(): OauthClientFormState {
  return {
    clientId: '',
    appCode: '',
    clientName: '',
    redirectUris: [{ uri: '', kind: 'LOGIN_CALLBACK' }],
    mfaRoles: [],
    accessTtl: String(DEFAULT_ACCESS_TOKEN_TTL_SECONDS),
    idTtl: String(DEFAULT_ID_TOKEN_TTL_SECONDS),
    refreshEnabled: false,
    refreshAbsoluteTtl: String(DEFAULT_REFRESH_ABSOLUTE_TTL_SECONDS),
    refreshIdleTtl: String(DEFAULT_REFRESH_IDLE_TTL_SECONDS),
    requireAppRole: true,
    active: true,
  };
}

export function formFromOauthClient(client: OauthClient): OauthClientFormState {
  return {
    clientId: client.client_id,
    appCode: client.app_code,
    clientName: client.client_name,
    redirectUris: client.redirect_uris.map((entry) => ({ ...entry })),
    mfaRoles: [...client.mfa_roles],
    accessTtl: String(client.access_token_ttl_seconds),
    idTtl: String(client.id_token_ttl_seconds),
    refreshEnabled: client.refresh_enabled,
    refreshAbsoluteTtl:
      client.refresh_token_absolute_ttl_seconds === null
        ? String(DEFAULT_REFRESH_ABSOLUTE_TTL_SECONDS)
        : String(client.refresh_token_absolute_ttl_seconds),
    refreshIdleTtl:
      client.refresh_token_idle_ttl_seconds === null
        ? String(DEFAULT_REFRESH_IDLE_TTL_SECONDS)
        : String(client.refresh_token_idle_ttl_seconds),
    requireAppRole: client.require_app_role,
    active: client.active,
  };
}

function positiveInteger(raw: string): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const value = Number(trimmed);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

export type FormResult<T> =
  | { readonly ok: true; readonly body: T }
  | { readonly ok: false; readonly message: string };

type SharedFields = Omit<UpdateOauthClientBody, 'active'>;

function sharedFields(form: OauthClientFormState): FormResult<SharedFields> {
  const redirectUris: OauthClientRedirectUri[] = [];
  for (const entry of form.redirectUris) {
    const uri = entry.uri.trim();
    if (uri !== '') {
      redirectUris.push({ uri, kind: entry.kind });
    }
  }
  if (!redirectUris.some((entry) => entry.kind === 'LOGIN_CALLBACK')) {
    return { ok: false, message: 'ต้องมี redirect URI ชนิด Callback หลังล็อกอินอย่างน้อยหนึ่งรายการ' };
  }
  if (new Set(redirectUris.map((entry) => `${entry.kind}|${entry.uri}`)).size !== redirectUris.length) {
    return { ok: false, message: 'redirect URI ซ้ำกัน' };
  }

  const accessTtl = positiveInteger(form.accessTtl);
  const idTtl = positiveInteger(form.idTtl);
  if (accessTtl === null || idTtl === null) {
    return { ok: false, message: 'อายุ access token และ ID token ต้องเป็นจำนวนเต็มวินาทีที่มากกว่า 0' };
  }

  let absolute: number | null = null;
  let idle: number | null = null;
  if (form.refreshEnabled) {
    absolute = positiveInteger(form.refreshAbsoluteTtl);
    idle = positiveInteger(form.refreshIdleTtl);
    if (absolute === null || idle === null) {
      return {
        ok: false,
        message: 'เปิด refresh token แล้ว อายุสูงสุดและอายุเมื่อไม่ใช้งานต้องเป็นจำนวนเต็มวินาทีที่มากกว่า 0',
      };
    }
  }

  return {
    ok: true,
    body: {
      redirect_uris: redirectUris,
      mfa_roles: [...form.mfaRoles],
      access_token_ttl_seconds: accessTtl,
      id_token_ttl_seconds: idTtl,
      refresh_enabled: form.refreshEnabled,
      refresh_token_absolute_ttl_seconds: absolute,
      refresh_token_idle_ttl_seconds: idle,
      require_app_role: form.requireAppRole,
    },
  };
}

export function toCreateOauthClientBody(
  form: OauthClientFormState,
): FormResult<CreateOauthClientBody> {
  const clientId = form.clientId.trim();
  const appCode = form.appCode.trim();
  const clientName = form.clientName.trim();
  if (clientId === '' || appCode === '' || clientName === '') {
    return { ok: false, message: 'กรอก client_id, แอป และชื่อ client ให้ครบ' };
  }
  const shared = sharedFields(form);
  if (!shared.ok) {
    return shared;
  }
  return {
    ok: true,
    body: { client_id: clientId, app_code: appCode, client_name: clientName, ...shared.body },
  };
}

export function toUpdateOauthClientBody(
  form: OauthClientFormState,
): FormResult<UpdateOauthClientBody> {
  const shared = sharedFields(form);
  if (!shared.ok) {
    return shared;
  }
  return { ok: true, body: { ...shared.body, active: form.active } };
}

export function describeTtl(seconds: number): string {
  if (seconds % 3600 === 0) {
    return `${seconds / 3600} ชม.`;
  }
  if (seconds % 60 === 0) {
    return `${seconds / 60} นาที`;
  }
  return `${seconds} วินาที`;
}

export { REDIRECT_URI_KINDS };
